import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram } from 'lucide-react';

const Footer = ({ language }) => {
  const texts = {
    no: {
      company: 'Souschaif',
      tagline: 'Fremtiden for AI-drevet matlaging',
      quickLinks: 'Hurtige lenker',
      support: 'Support',
      legal: 'Juridisk',
      contact: 'Kontakt oss',
      address: 'Amsterdam, Nederland',
      email: 'help@amintabv.com',
      phone: '022120403',
      rights: 'Alle rettigheter forbeholdt.',
      clickzillas: 'Dette domenet og alle annonserettigheter er eiendom av Clickzillas B.V.',
      privacy: 'Personvernerklæring',
      terms: 'Vilkår og betingelser',
      cookies: 'Informasjonskapsler',
      serviceDescription: 'Souschaif er en digital tjeneste som gir brukeren oppskrifter og måltidsforslag basert på opplastede bilder eller informasjon om tilgjengelige ingredienser. Tjenesten bidrar til å redusere matsvinn, spare penger og inspirere til varierte måltider. Eventuelle produkt- eller merkeanbefalinger er ikke tilknyttet eller godkjent av de respektive merke- og produsenteiere.',
      howItWorks: 'Slik fungerer tjenesten Souschaif:',
      howItWorksText: 'Brukeren registrerer seg med sitt mobilnummer og bekrefter abonnementet ved å angi PIN-koden som blir sendt til mobilnummeret. Ved å gjøre dette bekrefter brukeren at han eller hun har forstått og akseptert betingelser og vilkår for å abonnere på tjenesten. Etter aktivering får brukeren tilgang til digitale oppskrifter, tips, og funksjoner som matsvinnkalkulator og anbefalinger.',
      subscription: 'Tjenesten er et abonnement som koster kr. 199,- per måned inkludert mva. Abonnementsbeløpet trekkes enten via mobilregningen eller kontantkortsaldo. Abonnementet fornyes automatisk fra måned til måned inntil brukeren selv sier det opp. For å avslutte abonnementet kan brukeren sende en SMS med SOUS STOPP til 2210, kontakte kundeservice på 022120403, eller sende en e-post til help@amintabv.com'
    },
    en: {
      company: 'Souschaif',
      tagline: 'The future of AI-driven cooking',
      quickLinks: 'Quick Links',
      support: 'Support',
      legal: 'Legal',
      contact: 'Contact Us',
      address: 'Amsterdam, Netherlands',
      email: 'help@amintabv.com',
      phone: '022120403',
      rights: 'All rights reserved.',
      clickzillas: 'This domain and all advertising rights are a property of Clickzillas B.V.',
      privacy: 'Privacy Policy',
      terms: 'Terms and Conditions',
      cookies: 'Cookies',
      serviceDescription: 'Souschaif is a digital service that provides users with recipes and meal suggestions based on uploaded images or information about available ingredients. The service helps reduce food waste, save money and inspire varied meals. Any product or brand recommendations are not affiliated with or endorsed by the respective brand and product owners.',
      howItWorks: 'How the Souschaif service works:',
      howItWorksText: 'The user registers with their mobile number and confirms the subscription by entering the PIN code that is sent to the mobile number. By doing this, the user confirms that they have understood and accepted the terms and conditions for subscribing to the service. After activation, the user gets access to digital recipes, tips, and features such as food waste calculator and recommendations.',
      subscription: 'The service is a subscription that costs NOK 199,- per month including VAT. The subscription amount is charged either via the mobile bill or prepaid balance. The subscription is automatically renewed from month to month until the user cancels it themselves. To cancel the subscription, the user can send an SMS with SOUS STOP to 2210, contact customer service at 022120403, or send an email to help@amintabv.com'
    }
  };

  const t = texts[language];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Service Description Section */}
        <div className="mb-12">
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-green-400 mb-4">{t.company}</h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              {t.serviceDescription}
            </p>
            
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-green-400">{t.howItWorks}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t.howItWorksText}
              </p>
              
              <p className="text-gray-300 text-sm leading-relaxed">
                {t.subscription}
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-green-400">{t.company}</h3>
            <p className="text-gray-300">{t.tagline}</p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-green-400 transition-colors duration-200">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-green-400 transition-colors duration-200">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-green-400 transition-colors duration-200">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.quickLinks}</h4>
            <ul className="space-y-2">
              <li><a href="#section-1" className="text-gray-300 hover:text-green-400 transition-colors duration-200">Hjem</a></li>
              <li><a href="#section-2" className="text-gray-300 hover:text-green-400 transition-colors duration-200">Funksjoner</a></li>
              <li><a href="#section-3" className="text-gray-300 hover:text-green-400 transition-colors duration-200">Hvordan det fungerer</a></li>
              <li><a href="#section-4" className="text-gray-300 hover:text-green-400 transition-colors duration-200">Fordeler</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.contact}</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">{t.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">{t.email}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">{t.phone}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm">
              <p>© 2024 {t.company}. {t.rights}</p>
              <p className="mt-1">{t.clickzillas}</p>
            </div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="/privacy.html" className="text-gray-400 hover:text-green-400 transition-colors duration-200 text-sm">
                {t.privacy}
              </a>
              <a href="/terms.html" className="text-gray-400 hover:text-green-400 transition-colors duration-200 text-sm">
                {t.terms}
              </a>
              <a href="#" className="text-gray-400 hover:text-green-400 transition-colors duration-200 text-sm">
                {t.cookies}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
